/**
 * Z-Index Token System
 * Centralized z-index values for consistent layering
 * 
 * Usage:
 * import { Z_INDEX } from '@/design-system';
 * <div className={Z_INDEX.modal}>...</div>
 */

export const Z_INDEX = {
  /** Base layer - z-0 */
  base: 'z-0',
  
  /** Dropdowns and autocomplete - z-10 */
  dropdown: 'z-10',
  
  /** Sticky headers - z-20 */
  sticky: 'z-20',
  
  /** Fixed elements like navbars - z-30 */
  fixed: 'z-30',
  
  /** Modal/dialog backdrop - z-40 */
  modalBackdrop: 'z-40',
  
  /** Modal/dialog content - z-50 */
  modal: 'z-50',
  
  /** Popovers overlay modals - z-50 */
  popover: 'z-50',
  
  /** Tooltips - above everything - z-[60] */
  tooltip: 'z-[60]',
  
  /** Toast notifications - z-[100] */
  toast: 'z-[100]',
  
  /** Command palette / spotlight - z-[110] */
  commandPalette: 'z-[110]',
  
  /** Maximum z-index for critical overlays - z-[9999] */
  max: 'z-[9999]',
} as const;

export type ZIndexToken = typeof Z_INDEX;
export type ZIndexKey = keyof ZIndexToken;

/**
 * Get the numeric z-index value from a token key
 */
export function getZIndexValue(key: ZIndexKey): number {
  const mapping: Record<ZIndexKey, number> = {
    base: 0,
    dropdown: 10,
    sticky: 20,
    fixed: 30,
    modalBackdrop: 40,
    modal: 50,
    popover: 50,
    tooltip: 60,
    toast: 100,
    commandPalette: 110,
    max: 9999,
  };
  return mapping[key];
}
